// src/components/ui/WavyBackground/WavyBackground.tsx

/**
 * @fileoverview WavyBackground component
 * @description Renders an animated wavy background with customizable gradients and glass morphism effects.
 * Optimized for performance with proper canvas management and efficient animation loops.
 * @module ui/WavyBackground/WavyBackground
 */

"use client"

import { 
  useEffect, 
  useRef, 
  useState, 
  useMemo, 
  useCallback, 
  memo,
  type FC,
  type ComponentPropsWithoutRef 
} from "react"
import { createNoise3D, type NoiseFunction3D } from "simplex-noise"
import { cn } from "~/lib/utils"
import { createDeterministicRandom } from "~/lib/utils/random"
import { ColorGradientSelector, DEFAULT_COLORS } from "./ColorGradientSelector"
import { throttle } from "~/lib/throttle"

/*
 * Deterministic noise seeding. `createNoise3D()` defaults to `Math.random` to
 * build its permutation table, and that call runs in `useMemo` during the
 * server prerender of this client component — which Cache Components forbids
 * (`next-prerender-random-client`). Seeding the noise with the shared
 * `createDeterministicRandom` PRNG (canonical mulberry32) makes the field
 * deterministic across server and client (no hydration drift) while keeping
 * the organic look; the animation comes from sampling the field over time, not
 * from the seed. (Was a local mulberry32 copy — byte-identical to the canonical
 * for this non-zero seed; see `lib/utils/__tests__/random.test.ts`.)
 */

/** Fixed seed for the wave noise field (any constant works; this is arbitrary). */
const WAVE_NOISE_SEED = 0x9e3779b9

/**
 * Type alias for gradient colors array
 * @type {string[]}
 */
type GradientColors = string[]

/**
 * Animation speed presets
 * @type {'slow' | 'fast'}
 */
type AnimationSpeed = 'slow' | 'fast'

/**
 * Default configuration constants
 * Centralized configuration to avoid magic numbers and improve maintainability
 * 
 * Performance considerations:
 * - lineCount: Limited to 5 to balance visual quality with rendering performance
 * - speedValues: Optimized for smooth animation without excessive CPU usage
 * - throttleDelay: 200ms prevents excessive resize calculations
 * - minFrameInterval: Caps at ~60fps to prevent unnecessary rendering overhead
 * - visibilityThreshold: 0.1 provides early visibility detection for smooth transitions
 * 
 * @type {Readonly<{lineCount: number, speedValues: Record<AnimationSpeed, number>, throttleDelay: number, minFrameInterval: number, visibilityThreshold: number}>}
 */
const DEFAULT_CONFIG = {
  lineCount: 5,
  speedValues: {
    slow: 0.00025,  // Slower animation reduces CPU usage
    fast: 0.0002,   // Still reasonable for performance
  } as const,
  throttleDelay: 200,        // Prevents excessive resize handling
  minFrameInterval: 16,      // ~60fps cap - higher rates waste resources
  visibilityThreshold: 0.1,  // Early detection for smooth user experience
} as const

/**
 * Maximum device pixel ratio for canvas backing stores.
 * Matches Globe.tsx — caps fill rate on 3× Retina / 4K displays without a visible loss.
 */
const MAX_CANVAS_DPR = 2

/**
 * Returns the effective canvas DPR, capped for animated-canvas performance.
 *
 * @returns {number} Device pixel ratio in the range [1, MAX_CANVAS_DPR].
 */
function getCanvasDpr(): number {
  if (typeof window === "undefined") return 1
  return Math.min(window.devicePixelRatio || 1, MAX_CANVAS_DPR)
}

/**
 * Configuration object for wave animation parameters
 * Centralized configuration for better maintainability and type safety
 * 
 * @interface WaveConfig
 * @property {number} speedValue - The speed value for the wave animation
 * @property {number} waveOpacity - The opacity value for the wave animation (0-1)
 * @property {number} maxWaveHeight - The maximum height value for the wave animation
 * @property {number} minWaveHeight - The minimum height value for the wave animation
 * @property {number} wavelength - The wavelength value for the wave animation
 * @property {string | number} containerHeight - The height value for the container
 * @property {number} lineCount - The number of lines for the wave animation
 * @property {number} blur - The blur value for the wave animation
 * @property {number} waveSegmentLength - The length value for the wave animation segments
 * @property {number} verticalShiftFactor - The vertical shift value for the wave animation
 * @property {number} waveFrequencyFactor - The frequency value for the wave animation
 * @property {number} waveAmplitudeFactor - The amplitude value for the wave animation
 * @property {number} noiseYFactor - The noise Y value for the wave animation
 */
interface WaveConfig {
  speedValue: number
  waveOpacity: number
  maxWaveHeight: number
  minWaveHeight: number
  wavelength: number
  containerHeight: string | number
  lineCount: number
  blur: number
  waveSegmentLength: number
  verticalShiftFactor: number
  waveFrequencyFactor: number
  waveAmplitudeFactor: number
  noiseYFactor: number
}

/**
 * Props for the WavyBackground component
 * @interface WavyBackgroundProps
 * @extends {Omit<ComponentPropsWithoutRef<'div'>, 'children'>}
 * @property {React.ReactNode} children - Child elements to render on top of the wavy background
 * @property {string[]} colors - Array of color strings for the gradient waves
 * @property {number} blur - Blur intensity for the wave effect
 * @property {AnimationSpeed} speed - Animation speed preset
 * @property {number} waveOpacity - Opacity of the wave layers
 * @property {number} maxWaveHeight - Maximum height of wave peaks
 * @property {number} minWaveHeight - Minimum height of wave valleys
 * @property {number} wavelength - Length of wave cycles
 * @property {string | number} containerHeight - Height of the container
 * @property {number} lineCount - Number of wave layers to render
 * @property {number} waveSegmentLength - Length of each wave segment for rendering
 * @property {boolean} glassEffect - Whether to apply glass morphism effect
 * @property {number} waveAmplitudeFactor - Factor controlling wave amplitude variation
 * @property {boolean} pauseOnHidden - Whether to pause animation when component is not visible
 * @property {(gradients: GradientColors[]) => void} onGradientChange - Callback fired when gradients change
 * @property {() => void} onAnimationStart - Callback fired when animation starts
 * @property {() => void} onAnimationStop - Callback fired when animation stops
 */
interface WavyBackgroundProps extends Omit<ComponentPropsWithoutRef<'div'>, 'children'> {
  /** Child elements to render on top of the wavy background */
  children?: React.ReactNode
  /** Array of color strings for the gradient waves */
  colors?: string[]
  /** Blur intensity for the wave effect */
  blur?: number
  /** Animation speed preset */
  speed?: AnimationSpeed
  /** Opacity of the wave layers */
  waveOpacity?: number
  /** Maximum height of wave peaks */
  maxWaveHeight?: number
  /** Minimum height of wave valleys */
  minWaveHeight?: number
  /** Length of wave cycles */
  wavelength?: number
  /** Height of the container */
  containerHeight?: string | number
  /** Number of wave layers to render */
  lineCount?: number
  /** Length of each wave segment for rendering */
  waveSegmentLength?: number
  /** Whether to apply glass morphism effect */
  glassEffect?: boolean
  /** Factor controlling wave amplitude variation */
  waveAmplitudeFactor?: number
  /** Whether to pause animation when component is not visible */
  pauseOnHidden?: boolean
  /** Callback fired when gradients change */
  onGradientChange?: (gradients: GradientColors[]) => void
  /** Callback fired when animation starts */
  onAnimationStart?: () => void
  /** Callback fired when animation stops */
  onAnimationStop?: () => void
}

/**
 * Return type for the useWaveAnimation hook
 * @interface UseWaveAnimationReturn
 * @property {React.RefObject<HTMLCanvasElement | null>} canvasRef - Canvas element reference
 * @property {React.RefObject<HTMLDivElement | null>} containerRef - Container element reference for intersection observer
 * @property {GradientColors[]} gradients - Current gradient colors
 * @property {boolean} gradientsReady - Whether gradients are ready for rendering
 * @property {(newGradients: GradientColors[]) => void} handleGradientChange - Gradient change handler
 * @property {boolean} isVisible - Whether component is visible in viewport
 */
interface UseWaveAnimationReturn {
  /** Canvas element reference */
  canvasRef: React.RefObject<HTMLCanvasElement | null>
  /** Container element reference for intersection observer */
  containerRef: React.RefObject<HTMLDivElement | null>
  /** Current gradient colors */
  gradients: GradientColors[]
  /** Whether gradients are ready for rendering */
  gradientsReady: boolean
  /** Gradient change handler */
  handleGradientChange: (newGradients: GradientColors[]) => void
  /** Whether component is visible in viewport */
  isVisible: boolean
}

/**
 * Custom hook for managing wave animation state and lifecycle
 * Encapsulates all animation logic for better testability and reusability
 * 
 * Performance optimizations implemented:
 * - Frame rate limiting to prevent excessive rendering
 * - Visibility-based animation pausing to save CPU when not needed
 * - Throttled resize handling to prevent layout thrashing
 * - Efficient canvas operations with batch rendering
 * - Memory leak prevention with proper cleanup
 * 
 * @param {WaveConfig} config - Wave configuration object
 * @param {NoiseFunction3D} noise - 3D noise function
 * @param {boolean} pauseOnHidden - Whether to pause animation when not visible
 * @param {(gradients: GradientColors[]) => void} [onGradientChange] - Gradient change callback
 * @param {() => void} [onAnimationStart] - Animation start callback
 * @param {() => void} [onAnimationStop] - Animation stop callback
 * @returns {UseWaveAnimationReturn} Hook state and methods
 */
const useWaveAnimation = (
  config: WaveConfig,
  noise: NoiseFunction3D,
  pauseOnHidden = true,
  onGradientChange?: (gradients: GradientColors[]) => void,
  onAnimationStart?: () => void,
  onAnimationStop?: () => void
): UseWaveAnimationReturn => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const animationIdRef = useRef<number>(undefined)
  const isAnimatingRef = useRef<boolean>(false)
  const lastFrameTimeRef = useRef<number>(0)
  const intersectionObserverRef = useRef<IntersectionObserver>(undefined)
  // Holds the latest `render` so the requestAnimationFrame loop recurses through a
  // ref instead of referencing `render` before its own declaration. This also fixes
  // a latent staleness bug: the loop always calls the current `render` (and its
  // current `drawFrame`), not the closure captured when the loop was first scheduled.
  const renderRef = useRef<() => void>(undefined)

  const [gradients, setGradients] = useState<GradientColors[]>([])
  const [gradientsReady, setGradientsReady] = useState<boolean>(false)
  const [isVisible, setIsVisible] = useState<boolean>(true)
  const [isDocumentVisible, setIsDocumentVisible] = useState<boolean>(true)

  /**
   * Handles gradient color changes from the color selector
   * Memoized to prevent unnecessary re-renders when callback dependencies don't change
   * 
   * @function handleGradientChange
   * @param {GradientColors[]} newGradients - The new gradients to set
   * @returns {void}
   */
  const handleGradientChange = useCallback((newGradients: GradientColors[]): void => {
    setGradients(newGradients)
    setGradientsReady(true)
    onGradientChange?.(newGradients)
  }, [onGradientChange])

  /**
   * Draws animated wave layers on the canvas using simplex noise
   * 
   * Performance optimizations:
   * - Pre-calculates noise values to avoid redundant calculations
   * - Uses batch canvas operations with save/restore
   * - Optimized path drawing with efficient curve generation
   * - Gradient caching through canvas gradient objects
   * 
   * Potential overhead considerations:
   * - Pre-calculating all noise values uses more memory but reduces computation time
   * - Trade-off: Memory usage vs CPU cycles - beneficial for smooth animation
   * 
   * @function drawWaves
   * @param {CanvasRenderingContext2D} ctx - The canvas context
   * @param {number} w - The width of the canvas
   * @param {number} h - The height of the canvas
   * @param {number} nt - The current time
   * @returns {void}
   */
  const drawWaves = useCallback((ctx: CanvasRenderingContext2D, w: number, h: number, nt: number): void => {
    const baseAmplitude = (config.maxWaveHeight - config.minWaveHeight) / 2
    const centerY = h / 2

    // Batch canvas operations for better performance
    ctx.save()
    
    gradients.forEach((gradient, i) => {
      ctx.beginPath()

      const waveFrequency = 0.8 + (i / config.lineCount) * config.waveFrequencyFactor
      const positionFactor = i / (config.lineCount - 1)
      const waveAmplitude = baseAmplitude * (config.waveAmplitudeFactor + (1 - positionFactor) * 0.8)
      const verticalShift = i * (config.verticalShiftFactor * h / config.lineCount)

      // Adjust opacity based on positionFactor for depth effect
      ctx.globalAlpha = config.waveOpacity * (2 - positionFactor)

      // Pre-calculate noise values for better performance
      // Trade-off: Uses more memory but reduces redundant noise calculations
      const noiseValues: number[] = []
      const segmentCount = Math.ceil(w / config.waveSegmentLength) + 1 // +1 to ensure full coverage
      
      for (let segmentIndex = 0; segmentIndex < segmentCount; segmentIndex++) {
        const x = segmentIndex * config.waveSegmentLength
        const normalizedNoise = (noise(x / config.wavelength * waveFrequency, config.noiseYFactor * i, nt) + 1) / 2
        noiseValues.push(normalizedNoise)
      }

      // Draw top curve - using pre-calculated values
      noiseValues.forEach((normalizedNoise, index) => {
        const x = index * config.waveSegmentLength
        const yOffset = config.minWaveHeight + normalizedNoise * waveAmplitude
        const topY = centerY - yOffset + verticalShift

        if (index === 0) {
          ctx.moveTo(x, topY)
        } else {
          ctx.lineTo(x, topY)
        }
      })

      // Draw bottom curve (reverse direction for closed path)
      // Fixed: Added null check for array access safety
      for (let index = noiseValues.length - 1; index >= 0; index--) {
        const x = index * config.waveSegmentLength
        const normalizedNoise = noiseValues[index] // This is now guaranteed to exist
        if (normalizedNoise !== undefined) { // Additional safety check
          const yOffset = config.minWaveHeight + normalizedNoise * waveAmplitude
          const bottomY = centerY + yOffset + verticalShift
          ctx.lineTo(x, bottomY)
        }
      }

      ctx.closePath()

      // Apply gradient if available - canvas gradients are cached automatically
      if (gradient.length >= 2) {
        const waveGradient = ctx.createLinearGradient(0, 0, w, 0)
        gradient.forEach((color, index) => {
          waveGradient.addColorStop(index / (gradient.length - 1), color)
        })

        ctx.fillStyle = waveGradient
        ctx.fill()
      }
    })
    
    ctx.restore()
  }, [config, gradients, noise])

  /**
   * Initializes canvas dimensions based on container size
   * Handles high-DPI displays properly for crisp rendering
   * 
   * @function init
   * @returns {void}
   */
  const init = useCallback((): void => {
    const canvas = canvasRef.current
    if (canvas) {
      const rect = canvas.getBoundingClientRect()
      const dpr = getCanvasDpr()
      
      // Set actual size in memory (scaled to account for extra pixel density)
      canvas.width = rect.width * dpr
      canvas.height = rect.height * dpr
      
      // Scale the drawing context so everything will work at the higher ratio
      const ctx = canvas.getContext('2d')
      if (ctx) {
        ctx.scale(dpr, dpr)
      }
      
      // Set display size (css pixels)
      canvas.style.width = `${rect.width}px`
      canvas.style.height = `${rect.height}px`
    }
  }, [])

  /**
   * Renders a single frame of the wave animation with frame rate limiting
   * 
   * Performance optimization: Frame rate limiting prevents excessive rendering
   * - Caps at ~60fps to balance smoothness with CPU usage
   * - Uses performance.now() for high-precision timing
   * 
   * @function drawFrame
   * @param {number} currentTime - Current timestamp
   * @returns {void}
   */
  const drawFrame = useCallback((currentTime: number = performance.now()): void => {
    // Frame rate limiting - prevents excessive rendering overhead
    if (currentTime - lastFrameTimeRef.current < DEFAULT_CONFIG.minFrameInterval) {
      return
    }
    
    const canvas = canvasRef.current
    const ctx = canvas?.getContext("2d")
    if (canvas && ctx) {
      const dpr = getCanvasDpr()
      ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr)
      ctx.globalAlpha = config.waveOpacity
      drawWaves(ctx, canvas.width / dpr, canvas.height / dpr, currentTime * config.speedValue)
      lastFrameTimeRef.current = currentTime
    }
  }, [config, drawWaves])

  /**
   * Main render loop for continuous animation with visibility checks
   * Only runs when animation should be active to save CPU resources
   * 
   * @function render
   * @returns {void}
   */
  const render = useCallback((): void => {
    if (!isAnimatingRef.current) return

    const currentTime = performance.now()
    drawFrame(currentTime)
    // Recurse through the ref (kept in sync below) so the loop always picks up the
    // latest `render`/`drawFrame` and we don't reference `render` before declaration.
    animationIdRef.current = requestAnimationFrame(() => renderRef.current?.())
  }, [drawFrame])

  // Keep the rAF-loop pointer current after each commit (not during render).
  useEffect(() => {
    renderRef.current = render
  }, [render])

  /**
   * Throttled resize handler to prevent excessive re-renders
   * 
   * Performance consideration: 200ms throttle prevents layout thrashing
   * - Balances responsiveness with performance
   * - Prevents excessive canvas reinitialization during window resize
   */
  // `throttle` defers `init`/`drawFrame` (which read canvas refs) to resize events,
  // not render — so the ref reads are NOT during render. The rule can't see through
  // throttle's deferral, hence the targeted disable.
  // eslint-disable-next-line react-hooks/refs -- ref reads run in the throttled resize handler, not during render
  const throttledResize = useMemo(() => throttle((): void => {
    init()
    drawFrame()
  }, DEFAULT_CONFIG.throttleDelay), [init, drawFrame])

  /**
   * Handles window resize events
   * @function handleResize
   * @returns {void}
   */
  const handleResize = useCallback((): void => {
    throttledResize()
  }, [throttledResize])

  /**
   * Starts the animation loop
   * Only starts if conditions are met to prevent unnecessary resource usage
   * 
   * @function startAnimation
   * @returns {void}
   */
  const startAnimation = useCallback((): void => {
    if (isAnimatingRef.current) return
    if (pauseOnHidden && (!isVisible || !isDocumentVisible)) return
    
    isAnimatingRef.current = true
    onAnimationStart?.()
    render()
  }, [isVisible, isDocumentVisible, pauseOnHidden, onAnimationStart, render])

  /**
   * Stops the animation loop
   * Properly cleans up animation frame to prevent memory leaks
   * 
   * @function stopAnimation
   * @returns {void}
   */
  const stopAnimation = useCallback((): void => {
    isAnimatingRef.current = false
    if (animationIdRef.current) {
      cancelAnimationFrame(animationIdRef.current)
      animationIdRef.current = undefined
    }
    onAnimationStop?.()
  }, [onAnimationStop])

  /**
   * Handles document visibility changes to pause/resume animation
   * Saves CPU resources when tab is not visible
   * 
   * @function handleVisibilityChange
   * @returns {void}
   */
  const handleVisibilityChange = useCallback((): void => {
    const isVisible = !document.hidden
    setIsDocumentVisible(isVisible)
    
    if (pauseOnHidden) {
      if (isVisible && gradientsReady) {
        startAnimation()
      } else {
        stopAnimation()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pauseOnHidden, gradientsReady])

  /**
   * Effect to set up intersection observer for visibility detection
   * 
   * Performance benefit: Only animates when component is visible
   * - Saves CPU resources when component is off-screen
   * - Uses 0.1 threshold for early detection and smooth transitions
   */
  useEffect(() => {
    if (!pauseOnHidden || !containerRef.current) return

    intersectionObserverRef.current = new IntersectionObserver(
      (entries) => {
        // Fixed: Added null check for entries array safety
        if (entries.length > 0) {
          const entry = entries[0]
          if (entry) { // Additional safety check
            setIsVisible(entry.isIntersecting)
          }
        }
      },
      { threshold: DEFAULT_CONFIG.visibilityThreshold }
    )

    intersectionObserverRef.current.observe(containerRef.current)

    return () => {
      intersectionObserverRef.current?.disconnect()
    }
  }, [pauseOnHidden])

  /**
   * Effect to handle visibility-based animation control
   * Automatically starts/stops animation based on visibility state
   */
  useEffect(() => {
    if (!gradientsReady) return

    if (pauseOnHidden) {
      if (isVisible && isDocumentVisible) {
        startAnimation()
      } else {
        stopAnimation()
      }
    } else {
      startAnimation()
    }
  }, [isVisible, isDocumentVisible, gradientsReady, pauseOnHidden, startAnimation, stopAnimation])

  /**
   * Effect to initialize and manage the wave animation lifecycle
   * Handles setup, event listeners, and cleanup
   */
  useEffect(() => {
    if (!gradientsReady) return

    init()

    const handleResizeEvent = handleResize as EventListener
    const handleVisibilityChangeEvent = handleVisibilityChange as EventListener

    window.addEventListener("resize", handleResizeEvent)
    document.addEventListener("visibilitychange", handleVisibilityChangeEvent)

    return (): void => {
      stopAnimation()
      // Cancel any pending throttled resize so it can't init()/drawFrame() on a
      // torn-down canvas after unmount (the prior lodash version didn't do this).
      throttledResize.cancel()
      window.removeEventListener("resize", handleResizeEvent)
      document.removeEventListener("visibilitychange", handleVisibilityChangeEvent)
      intersectionObserverRef.current?.disconnect()
    }
  }, [gradientsReady, init, handleResize, handleVisibilityChange, stopAnimation, throttledResize])

  return {
    canvasRef,
    containerRef,
    gradients,
    gradientsReady,
    handleGradientChange,
    isVisible
  }
}

/**
 * A React component that renders animated wavy background with customizable gradients
 * and glass morphism effects. Uses simplex noise for smooth, organic wave motion.
 * 
 * Features:
 * - Hardware-accelerated canvas animations
 * - Customizable wave parameters and colors
 * - Glass morphism effects with backdrop blur
 * - Responsive design with proper resize handling
 * - Performance optimized with efficient rendering
 * - Visibility-based animation pausing
 * - Frame rate limiting and throttling
 * - Fully typed with TypeScript
 * - Composable with standard div props
 * 
 * Performance optimizations:
 * - Frame rate limiting to ~60fps
 * - Visibility-based animation pausing
 * - Throttled resize handling
 * - Efficient canvas operations
 * - Memory leak prevention
 * - Hardware acceleration hints
 * 
 * @example
 * ```tsx
 * <WavyBackground
 *   colors={["#ff0000", "#00ff00", "#0000ff"]}
 *   speed="slow"
 *   glassEffect={true}
 *   pauseOnHidden={true}
 *   onGradientChange={(gradients) => console.log('Gradients changed:', gradients)}
 * >
 *   <div>Your content here</div>
 * </WavyBackground>
 * ```
 * 
 * @param {WavyBackgroundProps} props - Component props
 * @returns {JSX.Element} The rendered component
 */
export const WavyBackground: FC<WavyBackgroundProps> = memo(({
  children,
  className,
  colors = DEFAULT_COLORS,
  blur = 5,
  speed = "slow",
  waveOpacity = 0.7,
  maxWaveHeight = 650,
  minWaveHeight = 75,
  wavelength = 1200,
  containerHeight = '100%',
  lineCount = DEFAULT_CONFIG.lineCount,
  waveSegmentLength = 3,
  glassEffect = true,
  waveAmplitudeFactor = 0.4,
  pauseOnHidden = true,
  onGradientChange,
  onAnimationStart,
  onAnimationStop,
  ...divProps
}) => {
  /**
   * Memoized 3D noise function for consistent wave generation
   * Created once and reused to avoid recreation overhead
   */
  const noise = useMemo(() => createNoise3D(createDeterministicRandom(WAVE_NOISE_SEED)), [])

  /**
   * Memoized configuration object to prevent unnecessary re-renders
   * Only recreates when actual dependencies change
   * 
   * Performance benefit: Prevents useWaveAnimation from re-running unnecessarily
   */
  const config = useMemo<WaveConfig>(() => ({
    speedValue: DEFAULT_CONFIG.speedValues[speed],
    waveOpacity,
    maxWaveHeight,
    minWaveHeight,
    wavelength,
    containerHeight,
    lineCount,
    blur,
    waveSegmentLength,
    verticalShiftFactor: 0,
    waveFrequencyFactor: 2,
    waveAmplitudeFactor,
    noiseYFactor: 0.3,
  }), [
    speed, 
    waveOpacity, 
    maxWaveHeight, 
    minWaveHeight, 
    wavelength, 
    containerHeight, 
    lineCount, 
    blur, 
    waveSegmentLength, 
    waveAmplitudeFactor
  ])

  const {
    canvasRef,
    containerRef,
    gradientsReady,
    handleGradientChange,
    isVisible: _isVisible
  } = useWaveAnimation(
    config,
    noise,
    pauseOnHidden,
    onGradientChange,
    onAnimationStart,
    onAnimationStop
  )

  return (
    <div 
      {...divProps}
      ref={containerRef}
      className={cn("relative w-full h-full overflow-hidden", className)}
      aria-label="Animated wavy background"
      role="img"
    >
      {gradientsReady && (
        <div className="absolute inset-0 w-full h-full">
          <canvas
            className={cn(
              "absolute inset-0 w-full h-full",
              "will-change-transform transform-gpu", // Hardware acceleration hints
              // `opacity-100`, not the invalid `opacity-99` (no such utility —
              // it was purged, so the canvas already rendered fully opaque).
              // Visually identical; valid-class cleanup only.
              glassEffect && "opacity-100"
            )}
            ref={canvasRef}
            id="canvas"
            style={{
              filter: `blur(${config.blur}px)`,
              willChange: 'filter' // Hint for browser optimization
            }}
            aria-hidden="true"
          />
          {glassEffect && (
            // Dropped the malformed `bg-transparent/10` (an opacity modifier on
            // an already-fully-transparent color is a no-op); the inset
            // box-shadow below provides the subtle glass tint. No visual change.
            <div
              className="absolute inset-0 backdrop-blur-xl dark:bg-blend-color"
              style={{
                boxShadow: "inset 0 0 0 0 rgba(255, 255, 255, 0.1)",
              }}
              aria-hidden="true"
            />
          )}
        </div>
      )}
      <ColorGradientSelector
        colors={colors}
        lineCount={lineCount}
        onColorChange={handleGradientChange}
      />
      <div className="relative z-10 w-full h-full">
        {children}
      </div>
    </div>
  )
})

WavyBackground.displayName = 'WavyBackground'

export default WavyBackground

/**
 * Type exports for external use
 */
export type { 
  WavyBackgroundProps, 
  WaveConfig, 
  GradientColors, 
  AnimationSpeed,
  UseWaveAnimationReturn 
}
